import { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Cpu } from 'lucide-react';

const PHASES = [
  'Analyzing impact…',
  'Detecting deadlines…',
  'Categorizing zones…',
  'Structuring your week…',
];

/** Onboarding Screen 3 — the "Magic Sort" loading moment. */
export function SortingScreen() {
  const [phase, setPhase] = useState(0);

  const [slow, setSlow] = useState(false);

  useEffect(() => {
    const t = setInterval(() => setPhase((p) => Math.min(p + 1, PHASES.length - 1)), 550);
    // A long wait must not look identical to a frozen app (CLAUDE.md §2 —
    // 9 Oct 2026, iOS sat on the last phase for ever with nothing to read).
    const s = setTimeout(() => setSlow(true), 12_000);
    return () => {
      clearInterval(t);
      clearTimeout(s);
    };
  }, []);

  return (
    <div className="mx-auto flex min-h-full w-full max-w-md flex-col items-center justify-center gap-8 px-8 py-16">
      <div className="relative flex h-56 w-full max-w-xs items-center justify-center overflow-hidden rounded-3xl border border-slate-200 dark:border-slate-800/60 bg-slate-50 dark:bg-slate-900/40">
        <div className="absolute inset-0 bg-gradient-to-t from-brand-500/10 to-transparent" />
        <motion.div
          animate={{ scale: [1, 1.08, 1], opacity: [0.85, 1, 0.85] }}
          transition={{ duration: 2.2, repeat: Infinity, ease: 'easeInOut' }}
          className="relative z-10 rounded-full border border-brand-300 dark:border-brand-500/30 bg-brand-600/20 p-6 text-brand-600 dark:text-brand-400 shadow-inner"
        >
          <Cpu size={40} />
        </motion.div>
      </div>

      <div className="space-y-2 text-center">
        <div className="h-7">
          <AnimatePresence mode="wait">
            <motion.h3
              key={phase}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              className="text-lg font-black tracking-tight text-slate-900 dark:text-white"
            >
              {PHASES[phase]}
            </motion.h3>
          </AnimatePresence>
        </div>
        <p className="text-xs text-slate-500 dark:text-slate-400">
          The ZoneIn engine is reorganizing your strategic matrix.
        </p>
        {slow && (
          <p className="text-xs text-amber-600 dark:text-amber-400">
            Still waiting for the AI… if it doesn&rsquo;t answer soon, ZoneIn will sort on this
            device instead.
          </p>
        )}
      </div>

      <div className="flex gap-1.5">
        {PHASES.map((_, i) => (
          <span
            key={i}
            className={`h-1.5 w-6 rounded-full transition-colors duration-300 ${
              i <= phase ? 'bg-brand-500' : 'bg-slate-100 dark:bg-slate-800'
            }`}
          />
        ))}
      </div>
    </div>
  );
}
